// src/contexts/AuthContext.tsx
import { createContext, useContext } from 'react';

export interface Site {
  id: string;
  network_id: string;
  name: string;
  code: string;
  timezone: string;
  branding_name: string | null;
  is_active: boolean;
  networks?: {
    name: string;
    code: string;
  };
}

export interface Network {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'super_admin' | 'administrator' | 'basic_user';
  avatar_url?: string | null;
}

export interface AuthContextType {
  session: any;
  user: User | null;
  setUser: (user: User | null) => void;

  // Site context
  currentSite: Site | null;
  setCurrentSite: (site: Site | null) => void;
  availableSites: Site[];
  setAvailableSites: (sites: Site[]) => void;

  // Computed properties
  hasMultipleSites: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;

  // Site loading state
  sitesLoading: boolean;
  setSitesLoading: (loading: boolean) => void;
}

export const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  setUser: () => {},

  // Site context defaults
  currentSite: null,
  setCurrentSite: () => {},
  availableSites: [],
  setAvailableSites: () => {},

  // Computed defaults
  hasMultipleSites: false,
  isSuperAdmin: false,
  isAdmin: false,

  // Loading state
  sitesLoading: true,
  setSitesLoading: () => {},
});

// Custom hook for easier access
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
